import { cn } from '@/lib/utils';

// The shared filter chip: 24px high, a 4px radius and 12px text. A selected
// chip has a filled background. A count at the end of the label, for example
// "Active (3)", shows in mono. The text of the label does not change.
//   - 'outline' (default): an accent tint fills the selected chip.
//   - 'solid': the accent colour fills the selected chip.

export type FilterChipVariant = 'outline' | 'solid';

interface FilterChipProps {
    label: string;
    isSelected: boolean;
    onClick: () => void;
    variant?: FilterChipVariant;
    className?: string;
    /** Stable scenario handle, for a chip whose label also reads elsewhere on
     *  the page (a "Active" chip beside an "Active" stat card, say). It lands
     *  on the button itself, because a target resolves to the element it
     *  clicks. */
    testId?: string;
}

const COUNT_SUFFIX = /^(.*\S)\s+(\(\d[\d,]*\))$/;

const variantClasses: Record<FilterChipVariant, { base: string; selected: string; idle: string }> =
    {
        outline: {
            base: '',
            selected:
                'bg-[rgb(var(--color-accent)/0.15)] text-[rgb(var(--color-text-primary))] border-[rgb(var(--color-accent))]',
            idle: 'bg-transparent text-[rgb(var(--color-text-secondary))] border-[rgb(var(--color-divider))] hover:bg-[rgb(var(--color-surface-secondary))]',
        },
        solid: {
            base: '',
            selected: 'bg-[rgb(var(--color-accent))] border-[rgb(var(--color-accent))] text-white',
            idle: 'bg-[rgb(var(--color-surface-secondary))] border-[rgb(var(--color-divider))] text-[rgb(var(--color-text-secondary))] hover:border-[rgb(var(--color-accent)/0.5)]',
        },
    };

export default function FilterChip({
    label,
    isSelected,
    onClick,
    variant = 'outline',
    className,
    testId,
}: FilterChipProps) {
    const v = variantClasses[variant];
    const match = COUNT_SUFFIX.exec(label);
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            aria-pressed={isSelected}
            className={cn(
                'inline-flex h-6 items-center rounded-[4px] border px-2 text-annotation transition-colors whitespace-nowrap pointer-coarse:h-8',
                v.base,
                isSelected ? v.selected : v.idle,
                className,
            )}
        >
            {match ? (
                <>
                    {match[1]}{' '}
                    <span className="ml-1 font-mono tabular-nums text-[11px] opacity-80">
                        {match[2]}
                    </span>
                </>
            ) : (
                label
            )}
        </button>
    );
}
